import { motion } from "framer-motion";
import { ArrowDownRight } from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_b5b811b2-a7ff-452a-9cfa-2f673fde2f06/artifacts/5t2w93oo_Simple%20Solutions.png";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) {
    const top = el.getBoundingClientRect().top + window.scrollY - 80;
    window.scrollTo({ top, behavior: "smooth" });
  }
};

export const Hero = () => {
  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-28 pb-20 overflow-hidden bg-white grain"
    >
      {/* Decorative grid */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(to right, #000 1px, transparent 1px), linear-gradient(to bottom, #000 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />
      </div>

      {/* Big red accent */}
      <div className="absolute -right-32 top-24 w-[420px] h-[420px] rounded-full bg-[#BF1828]/[0.04] blur-3xl pointer-events-none" />
      <div className="absolute left-1/3 bottom-10 w-[300px] h-[300px] rounded-full bg-[#BF1828]/[0.05] blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left content */}
          <div className="lg:col-span-8">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-3 mb-10 border border-[#BFBFBF]/70 px-4 py-2"
            >
              <span className="w-2 h-2 bg-[#BF1828] rounded-full animate-pulse" />
              <span
                data-testid="hero-eyebrow"
                className="text-[11px] tracking-[0.3em] uppercase text-[#595959] font-medium"
              >
                Agencia de Publicidad &middot; Diseño &middot; Marketing
              </span>
            </motion.div>

            <motion.h1
              data-testid="hero-headline"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-display font-bold tracking-tighter leading-[0.95] text-black text-[44px] sm:text-[64px] lg:text-[88px]"
            >
              BIENVENIDOS A{" "}
              <span className="block">
                <span className="text-[#BF1828]">SIMPLE SOLUTIONS</span>
              </span>
              <span className="block">ASESORES</span>
            </motion.h1>

            <motion.p
              data-testid="hero-description"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="mt-8 max-w-2xl font-body text-base sm:text-lg leading-relaxed text-[#595959] text-pretty"
            >
              DONDE LA CREATIVIDAD Y LA ESTRATEGIA SE ENCUENTRAN PARA DAR VIDA
              A CAMPAÑAS QUE IMPACTAN, INSPIRAN Y CONVIERTEN.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <button
                data-testid="hero-primary-cta"
                onClick={() => scrollTo("contacto")}
                className="group inline-flex items-center justify-center gap-3 bg-[#BF1828] text-white hover:bg-black transition-all duration-300 px-8 py-4 text-sm uppercase tracking-[0.2em] font-medium"
              >
                Trabajemos Juntos
                <ArrowDownRight
                  size={18}
                  className="transition-transform group-hover:translate-x-1 group-hover:translate-y-1"
                />
              </button>
              <button
                data-testid="hero-secondary-cta"
                onClick={() => scrollTo("servicios")}
                className="inline-flex items-center justify-center gap-3 border border-black text-black hover:bg-black hover:text-white transition-all duration-300 px-8 py-4 text-sm uppercase tracking-[0.2em] font-medium"
              >
                Ver Servicios
              </button>
            </motion.div>

            {/* Stats row */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.55 }}
              className="mt-16 grid grid-cols-3 gap-6 border-t border-[#BFBFBF]/60 pt-8 max-w-2xl"
              data-testid="hero-stats"
            >
              {[
                { k: "12+", v: "Industrias" },
                { k: "05", v: "Servicios" },
                { k: "100%", v: "Estrategia a la medida" },
              ].map((s) => (
                <div key={s.v}>
                  <div className="font-display text-3xl sm:text-4xl font-bold text-black">
                    {s.k}
                  </div>
                  <div className="mt-1 text-[11px] tracking-[0.2em] uppercase text-[#595959]">
                    {s.v}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right - logo card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-4 hidden lg:block"
          >
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-24 h-24 border-t-2 border-l-2 border-[#BF1828]" />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b-2 border-r-2 border-black" />
              <div className="relative bg-white border border-[#BFBFBF]/60 p-10 flex flex-col items-center">
                <img
                  src={LOGO_URL}
                  alt="Simple Solutions"
                  className="w-full max-w-[260px] object-contain"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
